// @ts-strict-ignore
import { VehicleGroup } from '@fc-core';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { SortBy } from '@fc-shared/ui/sort-by/sort-by';

import * as fromGroups from '../reducers/vehicle-group.reducer';
import { getAllRawVehicles } from './vehicle.selectors';
import { selectRouterParam } from '@fc-store/selectors/router.selectors';
import * as fromVehicleGroup from '@fc-store/reducers/vehicle-group.reducer';

export const getVehicleGroupState =
  createFeatureSelector<fromVehicleGroup.State>(
    fromVehicleGroup.vehicleGroupsFeatureKey,
  );
export const getGroupEntities = createSelector(
  getVehicleGroupState,
  fromGroups.selectEntities,
);

export const getSelectedGroup = createSelector(
  getGroupEntities,
  selectRouterParam('id'),
  (entities, selectedId) => {
    return selectedId && entities[selectedId];
  },
);

export const getVehicleGroupFilterId = createSelector(
  getVehicleGroupState,
  fromGroups.getVehicleGroupFilterId,
);

export const getSelectedFilterGroup = createSelector(
  getGroupEntities,
  getVehicleGroupFilterId,
  (entities, selectedId) => {
    if (selectedId === -1) {
      return null;
    }
    return selectedId && entities[selectedId];
  },
);

export const getVehicleGroupSortByList = createSelector(
  getVehicleGroupState,
  fromGroups.getVehicleGroupSortByList,
);
export const getGroupSearchText = createSelector(
  getVehicleGroupState,
  fromGroups.getGroupSearchText,
);
export const getDefaultColors = createSelector(
  getVehicleGroupState,
  fromGroups.getDefaultColors,
);
export const getGroupsError = createSelector(
  getVehicleGroupState,
  fromGroups.getGroupsError,
);
export const getVehicleGroupsLoading = createSelector(
  getVehicleGroupState,
  fromGroups.getGroupsLoading,
);
export const getRawVehicleGroups = createSelector(
  getVehicleGroupState,
  fromGroups.selectAll,
);
export const getVehicleGroupSortBy = createSelector(
  getVehicleGroupState,
  fromGroups.getVehicleGroupSortBy,
);

export const getAllVehicleGroups = createSelector(
  getRawVehicleGroups,
  getVehicleGroupSortBy,
  getGroupSearchText,
  (vehicleGroups, sortBy, searchText) => {
    vehicleGroups = sortGroups(vehicleGroups, sortBy);
    if (searchText?.length === 0) return vehicleGroups;
    return vehicleGroups.filter((group) =>
      group.name.toLocaleLowerCase().includes(searchText?.toLocaleLowerCase()),
    );
  },
);

export const getGroupById = (id: number) =>
  createSelector(getGroupEntities, (entities) => {
    return entities[+id];
  });

export const getVehiclesOutsideOfGroup = (id: number) => {
  return createSelector(
    getGroupById(id),
    getAllRawVehicles,
    (group, vehicles) => {
      if (!group) return [];
      return vehicles.filter((vehicle) => !group.vehicles.includes(vehicle.id));
    },
  );
};

export const getVehicleGroupSidebarList = () =>
  createSelector(
    getAllRawVehicles,
    getRawVehicleGroups,
    getVehicleGroupSortBy,
    getGroupSearchText,
    (vehicles, vehicleGroups, sortBy, searchText) => {
      vehicleGroups = vehicleGroups.map((group) => {
        return {
          ...group,
          vehicles: group.vehicles.filter((vehicle) =>
            vehicles.some((v) => v.id === vehicle),
          ),
        };
      });
      vehicleGroups = sortGroups(vehicleGroups, sortBy);
      if (searchText?.length === 0) return vehicleGroups;
      return vehicleGroups.filter((group) =>
        group.name
          .toLocaleLowerCase()
          .includes(searchText?.toLocaleLowerCase()),
      );
    },
  );

export const sortGroups = (
  groups: VehicleGroup[],
  sortBy: SortBy,
): VehicleGroup[] => {
  if ((!groups && !groups.length) || !sortBy) return [];
  if (sortBy.value === 'name') {
    return groups.sort((a, b) =>
      sortBy.order
        ? b.name.localeCompare(a.name)
        : a.name.localeCompare(b.name),
    );
  }
  if (sortBy.value === 'numbers') {
    return groups.sort((a, b) =>
      sortBy.order
        ? b.vehicles.length - a.vehicles.length
        : a.vehicles.length - b.vehicles.length,
    );
  }
  return groups;
};
